// Shared Risk Score Dashboard Cards
// Used by all 7 assessment modules — never define these inline in a module file.

import React, { useState } from 'react';
import Card from './Card';
import ReactSpeedometer from 'react-d3-speedometer';
import { getComplianceColor, getComplianceBarColor, RISK_GAUGE_SEGMENT_STOPS, RISK_GAUGE_SEGMENT_COLORS } from '../utils/riskColors';

// ── Shared helpers ─────────────────────────────────────────────────────────────

export const getRiskLevelColor = (level) => {
  switch ((level || '').toUpperCase()) {
    case 'CRITICAL': return 'text-red-700 bg-red-100';
    case 'HIGH':     return 'text-orange-600 bg-orange-100';
    case 'MEDIUM':   return 'text-yellow-600 bg-yellow-100';
    case 'LOW':      return 'text-green-600 bg-green-100';
    default:         return 'text-gray-600 bg-gray-100';
  }
};

// Compliance-percentage colors (getComplianceColor / getComplianceBarColor) come from
// ../utils/riskColors — the shared source of truth used by the dashboards too.

// Footnote shown beneath the risk-score speedometer. Clarifies that the single gauge
// value is a cumulative figure across every active risk, not the severity of one risk.
// Reused by the module dashboards so the wording lives in one place.
export const RiskScoreFootnote = () => (
  <div className="text-[10px] text-gray-400 mt-2">
    Composite score across all active risks
  </div>
);

// ── Card 1: Overall Residual Risk Score (speedometer) ─────────────────────────
export const OverallRiskScoreCard = ({ analytics, title = 'Residual Risk Score' }) => {
  const { residual_risk = 0, active_risks = 0, total_risks = 0 } = analytics || {};
  const hasAssessment = total_risks > 0;
  const validScore = typeof residual_risk === 'number' && !isNaN(residual_risk)
    ? Math.max(0, Math.min(100, residual_risk))
    : 0;

  const level = validScore > 75 ? 'CRITICAL'
    : validScore > 50 ? 'HIGH'
    : validScore > 25 ? 'MEDIUM'
    : 'LOW';

  return (
    <Card className="text-center p-4">
      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">{title}</h3>
      <div className="w-full flex justify-center mb-4">
        <ReactSpeedometer
          maxValue={100}
          value={hasAssessment ? validScore : 0}
          needleColor="#1f2937"
          customSegmentStops={RISK_GAUGE_SEGMENT_STOPS}
          segmentColors={RISK_GAUGE_SEGMENT_COLORS}
          segments={RISK_GAUGE_SEGMENT_COLORS.length}
          width={250}
          height={150}
          ringWidth={25}
          needleTransitionDuration={1000}
          needleTransition="easeElastic"
          currentValueText={hasAssessment ? `${validScore}%` : ' '}
          needleHeightRatio={0.5}
        />
      </div>
      {hasAssessment ? (
        <>
          <div className="flex justify-center space-x-2 mb-2">
            <div className={`text-xs px-2 py-1 rounded ${getRiskLevelColor(level)}`}>{level}</div>
          </div>
          <div className="text-xs text-gray-500">{active_risks || 0} Active Risks</div>
          <RiskScoreFootnote />
        </>
      ) : (
        <div className="text-xs text-gray-400 italic">Assessment Not Started</div>
      )}
    </Card>
  );
};

// ── Card 2: Control Effectiveness ─────────────────────────────────────────────
export const ControlEffectivenessCard = ({ analytics }) => {
  const ce           = analytics?.control_effectiveness ?? 0;
  const breakdown    = analytics?.ce_breakdown;
  // control_effectiveness === null is an explicit "no assessment behind this number" signal (Vendor
  // Dashboard sets it for vendors with no TPRM assessment). Without it, a vendor whose only risks are
  // integration-sourced (DarkWeb/ESM) would trip total_risks > 0 and read as "0.0% — Minimal", i.e.
  // assessed and failing, when it was never assessed at all. The engine always emits a number, so
  // the TPRM assessment page is unaffected.
  const hasData      = analytics?.control_effectiveness !== null
    && ((analytics?.total_risks ?? 0) > 0 || (analytics?.total_applicable_responses ?? 0) > 0);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const getMeta = (v) => {
    if (v >= 80) return { label: 'Near Maximum', bar: 'bg-green-500', badge: 'text-green-700 bg-green-100' };
    if (v >= 60) return { label: 'High',         bar: 'bg-blue-500',  badge: 'text-blue-700 bg-blue-100'  };
    if (v >= 40) return { label: 'Moderate',     bar: 'bg-yellow-500',badge: 'text-yellow-700 bg-yellow-100' };
    if (v >= 20) return { label: 'Low',          bar: 'bg-orange-500',badge: 'text-orange-700 bg-orange-100' };
    return         { label: 'Minimal',       bar: 'bg-red-500',   badge: 'text-red-700 bg-red-100'       };
  };
  const { label, bar, badge } = getMeta(Math.round(ce));

  return (
    <Card className="p-4">
      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Control Effectiveness</h3>
      {hasData ? (
        <>
          <div className="flex items-end gap-1 mb-1">
            <span className="text-4xl font-bold text-gray-800">{ce.toFixed(1)}</span>
            <span className="text-lg font-medium text-gray-500 mb-1">%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
            <div className={`${bar} h-2 rounded-full transition-all duration-500`} style={{ width: `${Math.min(100, ce)}%` }} />
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${badge}`}>{label}</span>
            <span className="text-xs text-gray-400">{analytics?.total_applicable_responses ?? 0} questions</span>
          </div>
          {breakdown && (
            <div className="mt-2">
              <button
                onClick={() => setShowBreakdown(v => !v)}
                className="w-full flex items-center justify-between text-xs text-gray-400 hover:text-gray-600 border-t border-gray-100 pt-2 mt-1 transition-colors"
              >
                <span className="font-medium tracking-wide">Score Breakdown</span>
                <span>{showBreakdown ? '▲' : '▼'}</span>
              </button>
              {showBreakdown && (
                <div className="mt-1.5 text-xs space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-600 leading-tight">Yes × {breakdown.yesCount}</div>
                      <div className="text-gray-400 text-[10px] font-mono leading-tight">n×100</div>
                    </div>
                    <span className="font-mono font-semibold flex-shrink-0 text-green-600">+{breakdown.yesCount * 100}</span>
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-600 leading-tight">Partially × {breakdown.partiallyCount}</div>
                      <div className="text-gray-400 text-[10px] font-mono leading-tight">n×50</div>
                    </div>
                    <span className="font-mono font-semibold flex-shrink-0 text-yellow-600">+{breakdown.partiallyCount * 50}</span>
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-600 leading-tight">No × {breakdown.noCount}</div>
                      <div className="text-gray-400 text-[10px] font-mono leading-tight">n×0</div>
                    </div>
                    <span className="font-mono font-semibold flex-shrink-0 text-gray-500">+0</span>
                  </div>
                  <div className="border-t border-dashed border-gray-200 pt-1.5 flex justify-between items-center">
                    <span className="text-gray-500">Raw CE ÷ {breakdown.totalApplicableResponses} questions</span>
                    <span className="font-mono font-semibold text-gray-700">{breakdown.rawCe}%</span>
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-600 leading-tight">Remediation credit ({breakdown.remediatedCount} of {breakdown.totalRisks} risks fixed)</div>
                      <div className="text-gray-400 text-[10px] font-mono leading-tight">ratio×(100−rawCE)</div>
                    </div>
                    <span className="font-mono font-semibold text-blue-600 flex-shrink-0">+{breakdown.remediationCredit}</span>
                  </div>
                  <div className="border-t border-gray-300 pt-1.5 flex justify-between items-center font-semibold">
                    <span className="text-gray-800">Control Effectiveness</span>
                    <span className="font-mono text-gray-900">{breakdown.ce}%</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="text-xs text-gray-400 italic mt-4">Assessment Not Started</div>
      )}
    </Card>
  );
};

// ── Card 3: Residual Risk (with score breakdown) ───────────────────────────────
export const ResidualRiskCard = ({ analytics }) => {
  const rrs       = analytics?.residual_risk ?? 0;
  const breakdown = analytics?.score_breakdown;
  const hasData   = (analytics?.total_risks ?? 0) > 0 || (analytics?.total_applicable_responses ?? 0) > 0;
  const [showBreakdown, setShowBreakdown] = useState(false);

  const getMeta = (v) => {
    if (v <= 15) return { label: 'Low Exposure', bar: 'bg-green-500',  badge: 'text-green-700 bg-green-100'   };
    if (v <= 35) return { label: 'Moderate',     bar: 'bg-yellow-500', badge: 'text-yellow-700 bg-yellow-100' };
    if (v <= 55) return { label: 'High',         bar: 'bg-orange-500', badge: 'text-orange-700 bg-orange-100' };
    if (v <= 75) return { label: 'Very High',    bar: 'bg-red-500',    badge: 'text-red-700 bg-red-100'       };
    return         { label: 'Critical',      bar: 'bg-red-700',    badge: 'text-red-800 bg-red-100'       };
  };
  const { label, bar, badge } = getMeta(rrs);

  const tierLabel = breakdown?.tier
    ? breakdown.tier.charAt(0) + breakdown.tier.slice(1).toLowerCase()
    : '—';
  const rows = breakdown ? [
    { key: 'CRITICAL', lbl: `Critical × ${breakdown.critCount}`, color: 'text-red-600'    },
    { key: 'HIGH',     lbl: `High × ${breakdown.highCount}`,     color: 'text-orange-600' },
    { key: 'MEDIUM',   lbl: `Medium × ${breakdown.medCount}`,    color: 'text-yellow-600' },
    { key: 'LOW',      lbl: `Low × ${breakdown.lowCount}`,       color: 'text-green-600'  },
  ] : [];

  return (
    <Card className="p-4">
      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Residual Risk</h3>
      {hasData ? (
        <>
          <div className="text-4xl font-bold text-gray-800 mb-1">{rrs.toFixed(1)}%</div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
            <div className={`${bar} h-2 rounded-full transition-all duration-500`} style={{ width: `${Math.min(100, rrs)}%` }} />
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${badge}`}>{label}</span>
            <span className="text-xs text-gray-400">{analytics?.scoring_version ?? 'v2'}</span>
          </div>
          {breakdown && (
            <div className="mt-2">
              <button
                onClick={() => setShowBreakdown(v => !v)}
                className="w-full flex items-center justify-between text-xs text-gray-400 hover:text-gray-600 border-t border-gray-100 pt-2 mt-1 transition-colors"
              >
                <span className="font-medium tracking-wide">Score Breakdown</span>
                <span>{showBreakdown ? '▲' : '▼'}</span>
              </button>
              {showBreakdown && (
                <div className="mt-1.5 text-xs space-y-1.5">
                  {rows.map(({ key, lbl, color }) => (
                    <div key={key} className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className={`leading-tight ${key === breakdown.tier ? 'text-gray-800 font-semibold' : 'text-gray-600'}`}>
                          {lbl}{key === breakdown.tier && <span className="text-gray-400 font-normal"> ← worst active severity</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="border-t border-dashed border-gray-200 pt-1.5 flex justify-between items-center">
                    <span className="text-gray-500">{tierLabel} anchor (band floor)</span>
                    <span className="font-mono font-semibold text-gray-700">{breakdown.anchor}</span>
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-600 leading-tight">Concentration bump</div>
                      <div className="text-gray-400 text-[10px] font-mono leading-tight">{breakdown.bumpCount} at {tierLabel.toLowerCase()} → climb toward next band</div>
                    </div>
                    <span className="font-mono font-semibold text-blue-600 flex-shrink-0">+{breakdown.bump}</span>
                  </div>
                  <div className="border-t border-gray-300 pt-1.5 flex justify-between items-center font-semibold">
                    <span className="text-gray-800">Residual Risk</span>
                    <span className="font-mono text-gray-900">{rrs.toFixed(1)}%</span>
                  </div>
                  <div className="border-t border-dashed border-gray-200 pt-1.5">
                    <div className="text-gray-400 text-[10px] uppercase tracking-wide mb-1">Band thresholds</div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-500">
                      <span>0–15 Low Exposure</span>
                      <span>16–35 Moderate</span>
                      <span>36–55 High</span>
                      <span>56–75 Very High</span>
                      <span>76–100 Critical</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="text-xs text-gray-400 italic mt-4">Assessment Not Started</div>
      )}
    </Card>
  );
};

// ── Card 4: Risk Density ───────────────────────────────────────────────────────
export const RiskDensityCard = ({ analytics }) => {
  const density     = analytics?.risk_density ?? 0;
  const activeRisks = analytics?.active_risks ?? 0;
  const totalQ      = analytics?.total_applicable_responses ?? 0;
  const externalRiskCount = analytics?.external_risk_count ?? 0;
  const breakdown   = analytics?.density_breakdown;
  const hasData     = totalQ > 0;
  const [showBreakdown, setShowBreakdown] = useState(false);

  const getMeta = (v) => {
    if (v <= 20) return { label: 'Low Concentration', bar: 'bg-green-500',  badge: 'text-green-700 bg-green-100'   };
    if (v <= 40) return { label: 'Moderate',          bar: 'bg-yellow-500', badge: 'text-yellow-700 bg-yellow-100' };
    if (v <= 60) return { label: 'High',              bar: 'bg-orange-500', badge: 'text-orange-700 bg-orange-100' };
    return         { label: 'Very High',          bar: 'bg-red-500',    badge: 'text-red-700 bg-red-100'       };
  };
  const { label, bar, badge } = getMeta(density);

  return (
    <Card className="p-4">
      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Risk Density</h3>
      {hasData ? (
        <>
          <div className="text-4xl font-bold text-gray-800 mb-1">{density.toFixed(1)}%</div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
            <div className={`${bar} h-2 rounded-full transition-all duration-500`} style={{ width: `${Math.min(100, density)}%` }} />
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${badge}`}>{label}</span>
            <span className="text-xs text-gray-400">
              {activeRisks} risks{externalRiskCount > 0 ? ` (${externalRiskCount} external)` : ''} / {totalQ} questions
            </span>
          </div>
          {breakdown && (
            <div className="mt-2">
              <button
                onClick={() => setShowBreakdown(v => !v)}
                className="w-full flex items-center justify-between text-xs text-gray-400 hover:text-gray-600 border-t border-gray-100 pt-2 mt-1 transition-colors"
              >
                <span className="font-medium tracking-wide">Score Breakdown</span>
                <span>{showBreakdown ? '▲' : '▼'}</span>
              </button>
              {showBreakdown && (
                <div className="mt-1.5 text-xs space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-600 leading-tight">Partially × {breakdown.partiallyCount}</div>
                      <div className="text-gray-400 text-[10px] font-mono leading-tight">n×0.5</div>
                    </div>
                    <span className="font-mono font-semibold flex-shrink-0 text-yellow-600">+{breakdown.partiallyWeight}</span>
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-600 leading-tight">No × {breakdown.noCount}</div>
                      <div className="text-gray-400 text-[10px] font-mono leading-tight">n×1.0</div>
                    </div>
                    <span className="font-mono font-semibold flex-shrink-0 text-red-600">+{breakdown.noWeight}</span>
                  </div>
                  <div className="border-t border-dashed border-gray-200 pt-1.5 flex justify-between items-center">
                    <span className="text-gray-500">Raw density ÷ {breakdown.totalApplicableResponses} questions</span>
                    <span className="font-mono font-semibold text-gray-700">{breakdown.rawDensity}%</span>
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-600 leading-tight">Still open ({breakdown.activeRisks} of {breakdown.totalRisks} risks)</div>
                      <div className="text-gray-400 text-[10px] font-mono leading-tight">active ÷ total risks</div>
                    </div>
                    <span className="font-mono font-semibold text-blue-600 flex-shrink-0">×{breakdown.remediationFactor}</span>
                  </div>
                  <div className="border-t border-gray-300 pt-1.5 flex justify-between items-center font-semibold">
                    <span className="text-gray-800">Risk Density</span>
                    <span className="font-mono text-gray-900">{density.toFixed(1)}%</span>
                  </div>
                  <div className="border-t border-dashed border-gray-200 pt-1.5">
                    <div className="text-gray-400 text-[10px] uppercase tracking-wide mb-1">Band thresholds</div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-500">
                      <span>0–20 Low Concentration</span>
                      <span>21–40 Moderate</span>
                      <span>41–60 High</span>
                      <span>61–100 Very High</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="text-xs text-gray-400 italic mt-4">Assessment Not Started</div>
      )}
    </Card>
  );
};

// ── Card 5: Domain / Compliance Status ────────────────────────────────────────
// Prefers domain_scores (v2 calibrated) over raw compliance_status (v1 backend).
export const ComplianceStatusCard = ({ analytics, organizationAnalytics, activeDomain, onDomainClick, domainLabel = 'Domain' }) => {
  const usingDomainScores = organizationAnalytics?.domain_scores &&
    Object.keys(organizationAnalytics.domain_scores).length > 0;
  const complianceStatus = usingDomainScores
    ? organizationAnalytics.domain_scores
    : (analytics?.compliance_status || {});

  const domainEntries = Object.entries(complianceStatus || {})
    .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0));
  const stages = domainEntries.map(([framework]) => framework);
  const [showAllDomains, setShowAllDomains] = useState(false);
  const DOMAIN_LIMIT = 5;
  const hasAssessment = (organizationAnalytics?.total_risks || 0) > 0;

  return (
    <Card className="text-center">
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">{domainLabel}</h3>
        <div className="mt-0.5 text-xs text-gray-400">
          {activeDomain
            ? <span className="text-indigo-600 font-medium">Filtering: {activeDomain}</span>
            : 'Click a bar to filter risks'}
        </div>
      </div>

      {hasAssessment && stages.length > 0 ? (
        <>
          <div className="space-y-2">
            {domainEntries
              .slice(0, showAllDomains ? undefined : DOMAIN_LIMIT)
              .map(([framework, percentage]) => {
                // Floor the displayed score at 10% so every domain remains visible on the bar
                const validPct = typeof percentage === 'number' && !isNaN(percentage)
                  ? Math.max(10, Math.min(100, Math.round(percentage)))
                  : 10;
                const isActive = activeDomain === framework;
                return (
                  <button
                    key={framework}
                    onClick={() => onDomainClick?.(framework)}
                    className={`w-full text-left text-xs rounded px-1.5 py-1 transition-colors ${isActive ? 'bg-indigo-50 ring-1 ring-indigo-300' : 'hover:bg-gray-50'}`}
                    title={`Click to filter risks by "${framework}"`}
                  >
                    <div className="flex justify-between mb-1">
                      <span className={`font-medium ${isActive ? 'text-indigo-700' : 'text-gray-600'}`}>
                        {framework || 'Unknown'}
                      </span>
                      <span className={`font-medium ${getComplianceColor(validPct).split(' ')[0]}`}>
                        {validPct}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`${isActive ? 'bg-indigo-500' : getComplianceBarColor(validPct)} h-1.5 rounded-full transition-colors`}
                        style={{ width: `${validPct}%` }}
                      />
                    </div>
                  </button>
                );
              })}
          </div>
          {stages.length > DOMAIN_LIMIT && (
            <button
              onClick={() => setShowAllDomains(prev => !prev)}
              className="mt-3 w-full text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center justify-center gap-1 py-1 rounded hover:bg-indigo-50 transition-colors"
            >
              {showAllDomains ? <>▲ Show less</> : <>▼ +{stages.length - DOMAIN_LIMIT} more</>}
            </button>
          )}
        </>
      ) : (
        <>
          <div className="mb-4">
            <div className="text-3xl font-bold text-gray-300">—</div>
            <div className="text-xs text-gray-400">Average Risk</div>
          </div>
          <div className="space-y-2">
            {['Domain 1', 'Domain 2', 'Domain 3'].map((lbl) => (
              <div key={lbl} className="text-xs">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-300">{lbl}</span>
                  <span className="text-gray-300">—</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5" />
              </div>
            ))}
          </div>
          <div className="mt-4 text-xs text-gray-400 italic">Assessment Not Started</div>
        </>
      )}
    </Card>
  );
};

// ── Card 6: Vulnerability Distribution ────────────────────────────────────────
export const VulnerabilityDistributionCard = ({ analytics }) => {
  const { risk_distribution = {}, total_risks = 0, active_risks = 0, remediated_count = 0 } = analytics || {};
  const hasAssessment  = total_risks > 0;
  const criticalCount  = risk_distribution.CRITICAL       || 0;
  const highCount      = risk_distribution.HIGH           || 0;
  const mediumCount    = risk_distribution.MEDIUM         || 0;
  const lowCount       = risk_distribution.LOW            || 0;
  const infoCount      = risk_distribution.INFORMATIONAL  || 0;

  return (
    <Card className="text-center">
      <div className="mb-3">
        <h3 className="text-[13px] font-medium text-gray-500 uppercase tracking-wide">Vulnerability Distribution</h3>
      </div>

      {hasAssessment ? (
        <>
          <div className="space-y-1.5 mb-3">
            {criticalCount > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center"><div className="w-2.5 h-2.5 bg-red-700 rounded-full mr-2" /><span className="text-[13px] font-medium">Critical</span></div>
                <span className="text-[13px] font-bold text-red-700">{criticalCount}</span>
              </div>
            )}
            {highCount > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center"><div className="w-2.5 h-2.5 bg-orange-500 rounded-full mr-2" /><span className="text-[13px] font-medium">High</span></div>
                <span className="text-[13px] font-bold text-orange-500">{highCount}</span>
              </div>
            )}
            {mediumCount > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center"><div className="w-2.5 h-2.5 bg-yellow-500 rounded-full mr-2" /><span className="text-[13px] font-medium">Medium</span></div>
                <span className="text-[13px] font-bold text-yellow-600">{mediumCount}</span>
              </div>
            )}
            {lowCount > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center"><div className="w-2.5 h-2.5 bg-green-500 rounded-full mr-2" /><span className="text-[13px] font-medium">Low</span></div>
                <span className="text-[13px] font-bold text-green-600">{lowCount}</span>
              </div>
            )}
            {infoCount > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center"><div className="w-2.5 h-2.5 bg-blue-500 rounded-full mr-2" /><span className="text-[13px] font-medium">Info</span></div>
                <span className="text-[13px] font-bold text-blue-600">{infoCount}</span>
              </div>
            )}
          </div>
          <div className="border-t pt-2 space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Total Risks</span>
              <span className="font-bold text-gray-800">{total_risks}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Remediated</span>
              <span className="font-bold text-green-600">{remediated_count}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Active Risks</span>
              <span className="font-bold text-orange-600">{active_risks}</span>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="space-y-1.5 mb-3">
            {['Critical', 'High', 'Medium', 'Low'].map((lbl, i) => {
              const colors = ['bg-red-200', 'bg-orange-200', 'bg-yellow-200', 'bg-green-200'];
              return (
                <div key={lbl} className="flex justify-between items-center">
                  <div className="flex items-center">
                    <div className={`w-2.5 h-2.5 ${colors[i]} rounded-full mr-2`} />
                    <span className="text-[13px] font-medium text-gray-300">{lbl}</span>
                  </div>
                  <span className="text-[13px] font-bold text-gray-300">—</span>
                </div>
              );
            })}
          </div>
          <div className="border-t pt-2 space-y-1">
            <div className="flex justify-between text-xs"><span className="text-gray-400">Total Risks</span><span className="font-bold text-gray-300">—</span></div>
            <div className="flex justify-between text-xs"><span className="text-gray-400">Active Risks</span><span className="font-bold text-gray-300">—</span></div>
          </div>
          <div className="mt-3 text-xs text-gray-400 italic">Assessment Not Started</div>
        </>
      )}
    </Card>
  );
};

// ── Risk Sources: compact breakdown of a page's risks by source ───────────────
// Small companion to Vulnerability Distribution: pass `sources` as an ordered list of
// { label, count, dot, text } (dot = bg color class for the marker, text = text color class
// for the count). Renders an em-dash for every row when there are no risks yet.
export const RiskSourceBreakdownCard = ({ sources = [] }) => {
  const hasData = sources.some(s => (s.count || 0) > 0);
  return (
    <Card className="text-center">
      <div className="mb-3">
        <h3 className="text-[13px] font-medium text-gray-500 uppercase tracking-wide">Risk Sources</h3>
      </div>
      <div className="space-y-1.5">
        {sources.map(({ label, count, dot, text }) => (
          <div key={label} className="flex justify-between items-center">
            <div className="flex items-center">
              <div className={`w-2.5 h-2.5 ${dot} rounded-full mr-2`} />
              <span className="text-[13px] font-medium">{label}</span>
            </div>
            <span className={`text-[13px] font-bold ${text}`}>{hasData ? (count || 0) : '—'}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ── Card 7: Predictive Risk Timeline ─────────────────────────────────────────
// Aging-based projection: "Today" is the same posture score as the Residual Risk gauge; 45/90/180
// re-score the same active risks with tiers escalated per the backend's dwell table, assuming
// nothing gets remediated. data: { current, day45, day90, day180, escalating180 }.
export const PredictiveRiskBars = ({ data, loading, analytics }) => {
  const hasAssessment = (analytics?.total_risks || 0) > 0;

  if (loading) {
    return (
      <Card className="text-center h-full">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Predictive Risk Timeline</h3>
        <div className="text-sm text-gray-500 py-6">Loading...</div>
      </Card>
    );
  }

  const { current = 0, day45 = 0, day90 = 0, day180 = 0, escalating180 = 0 } = data || {};
  const clamp = (v) => (typeof v === 'number' && !isNaN(v) ? Math.max(0, Math.min(100, v)) : 0);
  const rows = [
    { label: 'Today',    value: clamp(current), barColor: 'bg-gray-400',   textColor: 'text-gray-700' },
    { label: '45 Days',  value: clamp(day45),   barColor: 'bg-yellow-500', textColor: 'text-yellow-700' },
    { label: '90 Days',  value: clamp(day90),   barColor: 'bg-orange-500', textColor: 'text-orange-700' },
    { label: '180 Days', value: clamp(day180),  barColor: 'bg-red-600',    textColor: 'text-red-700' },
  ];

  return (
    <Card className="text-center h-full">
      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Predictive Risk Timeline</h3>
      <div className="space-y-3">
        {rows.map(({ label, value, barColor, textColor }) => (
          <div key={label}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-600">{label}</span>
              <span className={`font-medium ${textColor}`}>{hasAssessment ? `${value.toFixed(1)}%` : '—'}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className={`${barColor} h-2 rounded-full transition-all duration-500`} style={{ width: hasAssessment ? `${value}%` : '0%' }} />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 text-xs text-gray-500">
        {!hasAssessment
          ? <span className="italic text-gray-400">Assessment Not Started</span>
          : escalating180 > 0
            ? `${escalating180} risk${escalating180 === 1 ? '' : 's'} projected to escalate in severity within 180 days if unremediated`
            : 'Risk escalation forecast if risks go unremediated'}
      </div>
    </Card>
  );
};
